import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Grid3X3,
  X,
} from 'lucide-react'
import QuestionViewer from '../components/QuestionViewer'
import { useQuestionIds, useQuestion } from '../hooks/usePYQ'
import type { Subject } from '../types'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'

export default function PYQBrowserPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Parse URL params
  const yearStart = parseInt(searchParams.get('yearStart') || '2016', 10)
  const yearEnd = parseInt(searchParams.get('yearEnd') || '2025', 10)
  const subject = searchParams.get('subject') as Subject | null
  const unitCode = searchParams.get('unitCode') || ''

  // Current question index (0-based)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showSolution, setShowSolution] = useState(false)
  const [showQuestionPicker, setShowQuestionPicker] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  // Step 1: Fetch all question IDs for the unit
  const {
    data: questionIds,
    isLoading: isLoadingIds,
    error: idsError,
  } = useQuestionIds(unitCode, !!subject && !!unitCode)

  const totalQuestions = questionIds?.length || 0
  const currentQuestionId = questionIds?.[currentIndex] || ''

  // Step 2: Fetch the current question's full details
  const {
    data: currentQuestion,
    isLoading: isLoadingQuestion,
    error: questionError,
    isFetching: isFetchingQuestion,
  } = useQuestion(currentQuestionId, !!currentQuestionId)

  const isLoading = isLoadingIds || isLoadingQuestion
  const error = idsError || questionError

  // Track ViewContent for FB Pixel
  useEffect(() => {
    if (subject && unitCode && totalQuestions > 0) {
      fbTrackViewContent('pyq_browser', unitCode, `PYQ: ${subject}`)
    }
  }, [subject, unitCode, totalQuestions])

  // Reset solution visibility when question changes
  useEffect(() => {
    setShowSolution(false)
  }, [currentIndex])

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowQuestionPicker(false)
      }
    }

    if (showQuestionPicker) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showQuestionPicker])

  // Handle question selection from picker
  const handleQuestionSelect = (index: number) => {
    setCurrentIndex(index)
    setShowQuestionPicker(false)
  }

  // Navigation handlers
  const goToFirst = () => setCurrentIndex(0)
  const goToPrev = () => setCurrentIndex((i) => Math.max(0, i - 1))
  const goToNext = () => setCurrentIndex((i) => Math.min(totalQuestions - 1, i + 1))
  const goToLast = () => setCurrentIndex(totalQuestions - 1)

  const goBack = () => navigate('/question-bank')

  // Validate params
  if (!subject || !unitCode) {
    return (
      <div className="min-h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-800">Missing filters</h2>
          <p className="text-gray-600 mt-1 mb-4">
            Please select filters to browse questions.
          </p>
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Filter Form
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-hidden">
      {/* Header - Fixed */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 z-10">
        <div className="flex items-center justify-between">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>

          <div className="text-center">
            <h1 className="text-lg font-semibold text-gray-900">
              {subject} - PYQ
            </h1>
            <p className="text-xs text-gray-500">
              {yearStart} - {yearEnd}
            </p>
          </div>

          <button
            onClick={() => setShowSolution(!showSolution)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              showSolution
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {showSolution ? (
              <>
                <EyeOff className="w-4 h-4" />
                Hide
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                Show
              </>
            )}
          </button>
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 min-h-0 overflow-y-auto">
        {isLoadingIds ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading questions...</p>
            </div>
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-800">
                Failed to load questions
              </h2>
              <p className="text-gray-600 mt-1">Please try again later.</p>
            </div>
          </div>
        ) : totalQuestions === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-800">
                No questions found
              </h2>
              <p className="text-gray-600 mt-1 mb-4">
                No questions are mapped to this unit yet.
              </p>
              <button
                onClick={goBack}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <ArrowLeft className="w-4 h-4" />
                Change Filters
              </button>
            </div>
          </div>
        ) : isLoadingQuestion || !currentQuestion ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading question details...</p>
            </div>
          </div>
        ) : (
          <div className="h-full relative">
            <QuestionViewer
              question={currentQuestion}
              questionNumber={currentIndex + 1}
              totalQuestions={totalQuestions}
              showSolution={showSolution}
            />
            {/* Loading overlay for transitions */}
            {isFetchingQuestion && !isLoadingQuestion && (
              <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            )}
          </div>
        )}
      </main>

      {/* Floating Question Picker Button */}
      {totalQuestions > 0 && (
        <button
          onClick={() => setShowQuestionPicker(true)}
          className="fixed bottom-24 right-4 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center z-20"
          aria-label="Select question"
        >
          <Grid3X3 className="w-5 h-5" />
        </button>
      )}

      {/* Question Picker Modal */}
      {showQuestionPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-30 p-4">
          <div
            ref={pickerRef}
            className="bg-white rounded-xl shadow-xl max-w-sm w-full max-h-[70vh] flex flex-col"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Select Question
              </h3>
              <button
                onClick={() => setShowQuestionPicker(false)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Question Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-5 gap-2">
                {Array.from({ length: totalQuestions }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => handleQuestionSelect(i)}
                    className={`w-full aspect-square rounded-lg text-sm font-medium transition-colors ${
                      i === currentIndex
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-3 border-t border-gray-200 text-center text-sm text-gray-500">
              {totalQuestions} questions total
            </div>
          </div>
        </div>
      )}

      {/* Footer Navigation - Fixed */}
      <nav className="flex-shrink-0 bg-white border-t border-gray-200 px-4 py-3 z-10">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button
            onClick={goToFirst}
            disabled={currentIndex === 0 || isLoading || totalQuestions === 0}
            className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronsLeft className="w-6 h-6" />
            <span className="text-xs font-medium">First</span>
          </button>

          <button
            onClick={goToPrev}
            disabled={currentIndex === 0 || isLoading || totalQuestions === 0}
            className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
            <span className="text-xs font-medium">Prev</span>
          </button>

          {/* Page indicator */}
          <div className="text-center">
            <span className="text-lg font-bold text-gray-900">
              {totalQuestions > 0 ? currentIndex + 1 : 0}
            </span>
            <span className="text-gray-400 mx-1">/</span>
            <span className="text-lg text-gray-500">{totalQuestions}</span>
          </div>

          <button
            onClick={goToNext}
            disabled={
              currentIndex >= totalQuestions - 1 || isLoading || totalQuestions === 0
            }
            className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
            <span className="text-xs font-medium">Next</span>
          </button>

          <button
            onClick={goToLast}
            disabled={
              currentIndex >= totalQuestions - 1 || isLoading || totalQuestions === 0
            }
            className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronsRight className="w-6 h-6" />
            <span className="text-xs font-medium">Last</span>
          </button>
        </div>
      </nav>
    </div>
  )
}
